/* LSG03 — PDP Buy Box A/B test, variation V1.
 *
 * The markup ships to 100% of traffic hidden, with every input disabled. This file is the only thing
 * that ever enables it, and only while Convert Experiences has the variation class on <body>. If this
 * script fails to load or throws, the page stays exactly as the control arm — that is deliberate.
 *
 * It never touches product.js. Size cards proxy to the theme's own variant radios and prices are
 * recomputed client-side from a JSON blob, so the shopper's plan and frequency survive a size change. */

if (!customElements.get('lsg03-buybox')) {
  class LSG03BuyBox extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.formId = this.dataset.form;
      this.variationClass = this.dataset.class || 'lsg03-buybox-v1';
      this.noUnitClass = this.dataset.classNoUnit || 'lsg035-buybox-v1';
      this.sizeHandle = this.dataset.sizeHandle;
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10) || 0;

      this.form = document.getElementById(this.formId);

      const price = document.getElementById('price-' + this.sectionId);
      this.themePrice = price && (price.closest('.product-price-container') || price);
      this.plans = this.readJSON('[data-lsg03-plans]') || {};
      this.strings = this.decodeStrings(this.readJSON('[data-lsg03-strings]') || {});

      this.sellingPlanInput = this.querySelector('[data-lsg03-selling-plan]');
      this.frequency = this.querySelector('[data-lsg03-frequency]');
      this.detail = this.querySelector('.lsg03-plan__detail');
      this.subTitle = this.querySelector('[data-lsg03-sub-title]');
      this.subPrice = this.querySelector('[data-lsg03-sub-price]');
      this.subCompare = this.querySelector('[data-lsg03-sub-compare]');
      this.onePrice = this.querySelector('[data-lsg03-one-price]');
      this.benefit1 = this.querySelector('[data-lsg03-benefit-1]');
      this.sizeValueLabel = this.querySelector('[data-lsg03-size-value]');
      this.sizeInputs = Array.from(this.querySelectorAll('.lsg03-size input'));
      this.planInputs = Array.from(this.querySelectorAll('.lsg03-plans input[type="radio"]'));

      this.hiddenControls = [];
      this.active = false;
      this.chosen = { id: null, groupId: null, name: null };
      this.currentVariantId = this.readVariantId();

      this.controller = new AbortController();
      this.bind();

      this.bodyObserver = new MutationObserver(() => this.sync());
      const watch = { attributes: true, attributeFilter: ['class'] };
      this.bodyObserver.observe(document.body, watch);
      this.bodyObserver.observe(document.documentElement, watch);

      this.sync();
    }

    disconnectedCallback() {
      this.controller.abort();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.formObserver) this.formObserver.disconnect();
      if (this.active) this.deactivate();
    }

    /* ------------------------------------------------------------------ setup */

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    // Liquid's `t` filter HTML-escapes, so "Subscribe & Save" arrives as "Subscribe &amp; Save". That is
    // correct when printed into markup, but these strings are assigned with textContent, which would
    // render the entity literally. Decoding once here keeps textContent (and its XSS safety) in use.
    decodeStrings(strings) {
      const decoder = document.createElement('textarea');
      Object.keys(strings).forEach((key) => {
        decoder.innerHTML = strings[key];
        strings[key] = decoder.value;
      });
      return strings;
    }

    readVariantId() {
      const input = this.form && this.form.querySelector('[name="id"]');
      return input ? String(input.value) : Object.keys(this.plans)[0];
    }

    bind() {
      const signal = this.controller.signal;

      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', (e) => this.onSizeChange(e), { signal });
      });
      this.planInputs.forEach((input) => {
        input.addEventListener('change', () => this.onPlanChange(), { signal });
      });
      if (this.frequency) {
        this.frequency.addEventListener('change', () => this.onFrequencyChange(), { signal });
      }

      document.addEventListener('product:variant-change', (e) => {
        if (!e.detail || e.detail.sectionId !== this.sectionId) return;
        this.currentVariantId = e.detail.variant ? String(e.detail.variant.id) : null;
        this.syncSizeFromVariant();
        this.render();
      }, { signal });

      // Capture phase on document, so this always runs before the target-phase submit handler that
      // product.js binds on the form itself.
      document.addEventListener('submit', () => {
        if (this.active) this.neutralize();
      }, { capture: true, signal });
    }

    /* ------------------------------------------------------- activation gate */

    // Accepts the variation class on <html> as well as <body>. Convert often executes in <head>, where
    // document.body does not exist yet, so <html> is the earliest element it can mark.
    isActive() {
      if (this.hasClass(this.variationClass)) return true;
      if (this.hidesUnits()) return true;
      // Preview escape hatch: works in the theme editor, where no Convert class exists.
      return new URLSearchParams(window.location.search).has('lsg03');
    }

    // LSG3.5: the same buy box as LSG03 without the per-carton price. Preview with ?lsg035.
    hidesUnits() {
      if (this.hasClass(this.noUnitClass)) return true;
      return new URLSearchParams(window.location.search).has('lsg035');
    }

    hasClass(name) {
      return document.documentElement.classList.contains(name) || document.body.classList.contains(name);
    }

    sync() {
      const should = this.isActive();
      // Re-evaluated on every class change, not just on activation: Convert can add the LSG3.5 class
      // after the LSG03 one has already activated the block, and the early return below would miss it.
      document.body.classList.toggle('lsg035-on', should && this.hidesUnits());
      if (should === this.active) return;
      this.active = should;
      if (should) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    activate() {
      // Only take over the theme's picker if we actually rendered a replacement. A default-variant
      // product renders no card row, so hiding it there would just leave a hole.
      const source = this.hasSizes() && this.getSourceFieldset();
      if (source) {
        // Hiding only the fieldset leaves <variant-selects> behind as an empty 10px .variations block.
        // Hide the whole wrapper when size is the only picker; with other options present, hide just
        // this fieldset so their pickers survive.
        const wrapper = document.getElementById('variant-selects-' + this.sectionId);
        const single = wrapper && wrapper.querySelectorAll('fieldset').length === 1;
        this.sizeSource = single ? wrapper : source;
        this.sizeSource.classList.add('lsg03-size-source');
      }

      this.sizeInputs.forEach((input) => { input.disabled = false; });
      this.planInputs.forEach((input) => { input.disabled = false; });
      if (this.frequency) this.frequency.disabled = false;

      this.hidden = false;
      document.body.classList.add('lsg03-on');

      this.takeOverControl();
      this.watchForm();
      this.render();
    }

    deactivate() {
      if (this.sizeSource) {
        this.sizeSource.classList.remove('lsg03-size-source');
        this.sizeSource = null;
      }


      this.sizeInputs.forEach((input) => { input.disabled = true; });
      this.planInputs.forEach((input) => { input.disabled = true; });
      if (this.frequency) this.frequency.disabled = true;
      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = true;
        this.sellingPlanInput.value = '';
      }

      this.hidden = true;
      if (this.formObserver) this.formObserver.disconnect();
      this.restoreControl();

      if (this.themePrice) this.themePrice.classList.remove('lsg03-price-hidden');

      if (!document.querySelector('lsg03-buybox:not([hidden])')) {
        document.body.classList.remove('lsg03-on');
      }
    }

    /* ------------------------------------------------ Recharge coexistence */

    // display:none is not enough — a hidden input still submits. The widget also loads asynchronously,
    // so this runs again from a MutationObserver and once more at submit time.
    takeOverControl() {
      const selector = 'rc-widget, rc-purchase-options, rc-purchase-option, rc-radio-icon,' +
        '[id*="recharge" i], [class*="recharge" i]';

      document.querySelectorAll(selector).forEach((node) => {
        if (this.contains(node)) return;

        // Prefer the app-block wrapper. Falling back to the node itself is only safe for a genuine
        // Recharge custom element — a loose class match could otherwise hide unrelated theme markup.
        const wrapper = node.closest('[id^="shopify-block-"]');
        const target = wrapper || (node.tagName.indexOf('RC-') === 0 ? node : null);
        if (!target || this.hiddenControls.indexOf(target) !== -1) return;

        target.classList.add('lsg03-hide-control');
        this.hiddenControls.push(target);
      });

      this.neutralize();
    }

    restoreControl() {
      this.hiddenControls.forEach((el) => el.classList.remove('lsg03-hide-control'));
      this.hiddenControls = [];
      document.querySelectorAll('[data-lsg03-neutralized]').forEach((el) => {
        el.disabled = false;
        delete el.dataset.lsg03Neutralized;
      });
    }

    neutralize() {
      if (!this.form) return;
      const candidates = new Set(Array.from(this.form.elements));
      document
        .querySelectorAll('[form="' + this.formId + '"]')
        .forEach((el) => candidates.add(el));

      candidates.forEach((el) => {
        if (el === this.sellingPlanInput || !el.name || el.disabled) return;
        if (!/^selling_plan|^subscription_id$|shipping_interval/.test(el.name)) return;
        el.disabled = true;
        el.dataset.lsg03Neutralized = '1';
      });
    }

    watchForm() {
      if (!this.form) return;
      const scope = this.form.closest('.thb-product-detail') || this.form;
      let queued = false;

      this.formObserver = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          if (this.active) this.takeOverControl();
        });
      });
      this.formObserver.observe(scope, { childList: true, subtree: true });
    }

    /* ------------------------------------------------------------ size proxy */

    hasSizes() {
      return this.dataset.hasSizes === 'true';
    }

    getSourceFieldset() {
      const scope = document.getElementById('variant-selects-' + this.sectionId);
      if (!scope) return null;
      return (
        scope.querySelector('fieldset[data-handle="' + this.sizeHandle + '"]') ||
        scope.querySelector('fieldset')
      );
    }

    // Drives the theme's own picker rather than reimplementing variant matching, so URL, media, price
    // and the sticky bar all update through the existing pipeline.
    onSizeChange(e) {
      const source = this.getSourceFieldset();
      if (!source) return;
      const value = e.target.value;

      const select = source.querySelector('select');
      if (select) {
        if (select.value === value) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const radio = Array.from(source.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === value
      );
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncSizeFromVariant() {
      const variant = this.plans[this.currentVariantId];
      if (!variant) return;
      const value = variant.options[this.sizeIndex];
      this.sizeInputs.forEach((input) => { input.checked = input.value === value; });
      if (this.sizeValueLabel) this.sizeValueLabel.textContent = value;
    }

    /* --------------------------------------------------------------- pricing */

    variantForSizeValue(value) {
      const current = this.plans[this.currentVariantId];
      if (!current) return null;
      const index = this.sizeIndex;
      return Object.keys(this.plans)
        .map((id) => this.plans[id])
        .find(
          (variant) =>
            variant.options[index] === value &&
            variant.options.every((option, i) => i === index || option === current.options[i])
        );
    }

    // Keeps the shopper's frequency across a size change: same plan id, else same group and interval
    // name, else the first allocation on the new variant.
    resolveAllocation(variant) {
      const allocations = (variant && variant.allocations) || [];
      if (!allocations.length) return null;
      return (
        allocations.find((a) => String(a.id) === String(this.chosen.id)) ||
        allocations.find((a) => a.group_id === this.chosen.groupId && a.name === this.chosen.name) ||
        allocations[0]
      );
    }

    isSubscribeSelected() {
      const input = this.querySelector('.lsg03-plan--subscribe input');
      return !!input && input.checked && this.subscribable !== false;
    }

    setSubscribable(canSubscribe) {
      this.subscribable = canSubscribe;

      const group = this.querySelector('.lsg03-plans');
      if (group) group.hidden = !canSubscribe;

      // Hide the theme's own price block, which the plan cards replace. Scoped to this section's
      // price element by id — the quick-view drawer has a .product-price-container too.
      if (this.themePrice) this.themePrice.classList.toggle('lsg03-price-hidden', canSubscribe);

      if (canSubscribe) return;

      const oneTime = this.querySelector('.lsg03-plan--onetime input');
      if (oneTime) oneTime.checked = true;
      if (this.detail) this.detail.hidden = true;
    }

    render() {
      const variant = this.plans[this.currentVariantId];
      if (!variant) {
        if (this.sellingPlanInput) this.sellingPlanInput.disabled = true;
        return;
      }

      // Not every variant is subscribable — on the Clean Protein flavours only Pack of 4 and Pack of 12
      // carry selling plans, Pack of 2 has none. With no plan to choose there is no choice to present,
      // so the whole group is hidden and one-time is forced. Leaving the Subscribe radio checked with a
      // blank price would tell the shopper they are subscribing while sending a one-time line item.
      this.setSubscribable((variant.allocations || []).length > 0);

      const allocation = this.resolveAllocation(variant);
      if (allocation) {
        this.chosen = { id: allocation.id, groupId: allocation.group_id, name: allocation.name };
      }

      this.renderFrequency(variant, allocation);
      this.renderPrices(variant, allocation);
      this.renderUnitPrices();
      this.applySellingPlan();
    }

    renderFrequency(variant, allocation) {
      if (!this.frequency) return;
      const allocations = variant.allocations || [];

      this.frequency.innerHTML = '';
      allocations.forEach((a) => {
        const option = document.createElement('option');
        option.value = String(a.id);
        option.textContent = a.name;
        this.frequency.appendChild(option);
      });
      if (allocation) this.frequency.value = String(allocation.id);
    }

    renderPrices(variant, allocation) {
      if (this.onePrice) this.onePrice.textContent = variant.price_money;

      if (!allocation) return;
      const percent = String(allocation.percent);

      if (this.subPrice) this.subPrice.textContent = allocation.price_money;
      if (this.subCompare) {
        this.subCompare.textContent = allocation.price < variant.price ? variant.price_money : '';
      }
      if (this.subTitle && this.strings.subscribe) {
        this.subTitle.textContent = this.strings.subscribe.replace('%%PERCENT%%', percent);
      }
      if (this.benefit1 && this.strings.benefit_1) {
        this.benefit1.textContent = this.strings.benefit_1.replace('%%PERCENT%%', percent);
      }
    }

    // Unit prices show only while a subscription is selected, and each card quotes its own subscribe
    // price — falling back to its one-time price for a size that has no plan. That reproduces the mock
    // exactly: with Subscribe active it reads $4.99 / $4.49 / $4.49, because Pack of 2 has no selling
    // plan (9.98/2) while Pack of 4 and 12 are discounted (19.96*0.9/4, 59.88*0.9/12). On One Time the
    // row clears rather than quoting subscribe pricing the shopper is not getting.
    renderUnitPrices() {
      // LSG3.5 never quotes a unit price; an empty line collapses via the :empty rule in the CSS.
      const subscribing = this.isSubscribeSelected() && !this.hidesUnits();

      this.sizeInputs.forEach((input) => {
        const target = input.parentElement.querySelector('[data-lsg03-unit]');
        if (!target) return;

        const variant = subscribing && this.variantForSizeValue(input.value);
        if (!variant) {
          target.textContent = '';
          return;
        }

        const allocation = this.resolveAllocation(variant);
        target.textContent = (allocation ? allocation.unit_money : variant.unit_money) || '';
      });
    }

    /* ------------------------------------------------------- selling_plan wiring */

    applySellingPlan() {
      if (!this.sellingPlanInput) return;
      const subscribing = this.isSubscribeSelected();

      if (this.detail) this.detail.hidden = !subscribing;

      // An empty selling_plan= is rejected by /cart/add.js, so the input is disabled rather than blanked.
      if (!this.active || !subscribing || !this.chosen.id) {
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
        return;
      }
      this.sellingPlanInput.value = String(this.chosen.id);
      this.sellingPlanInput.disabled = false;
    }

    onPlanChange() {
      this.renderUnitPrices();
      this.applySellingPlan();
    }

    onFrequencyChange() {
      const variant = this.plans[this.currentVariantId];
      const allocations = (variant && variant.allocations) || [];
      const allocation = allocations.find((a) => String(a.id) === this.frequency.value);
      if (!allocation) return;

      this.chosen = { id: allocation.id, groupId: allocation.group_id, name: allocation.name };
      this.renderPrices(variant, allocation);
      this.renderUnitPrices();
      this.applySellingPlan();
    }
  }

  customElements.define('lsg03-buybox', LSG03BuyBox);
}
